import { lazy, Suspense, useState, useEffect, type ReactNode } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
// import ComingSoon from "./components/ComingSoon";
// import ApiErrorPage from "./pages/ApiErrorPage";
import { ProtectedRoute, PublicRoute } from '@/contexts/RouteGuard';
import { AuthProvider } from '@/contexts/AuthContext';
import { LanguageProvider, useLanguage } from '@/contexts/LanguageContext';
import { CmsProvider } from '@/cms/CmsContext';
import type { CmsPage } from '@/cms/cmsBridge';
import { PUBLIC_API_BASE_URL } from "@/utils/publicApiConfig";
import { trackPageview } from "@/utils/siteAnalytics";

// Lazy load page components
const Index = lazy(() => import("./pages/Index"));
const Menu = lazy(() => import("./pages/Menu"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));
const Catering = lazy(() => import("./pages/Catering"));

const Admin = lazy(() => import("./pages/Admin"));
const Drinks = lazy(() => import("./pages/Drinks"));
const AdminLogin = lazy(() => import("./components/admin/LoginPage"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="lazy-component-loading">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
  </div>
);

const queryClient = new QueryClient();

const EDITABLE_PAGES: CmsPage[] = [
  { path: "/", label: "Home" },
  { path: "/menu", label: "Menu" },
  { path: "/drinks", label: "Drinks" },
  { path: "/about", label: "About" },
  { path: "/gallery", label: "Gallery" },
  { path: "/contact", label: "Contact" },
  { path: "/catering", label: "Catering" },
];

const CmsBridgeMount = ({ children }: { children: ReactNode }) => {
  const { currentLanguage, setLanguage } = useLanguage();
  const setCmsLanguage = (lang: string) => {
    if (lang === "sv" || lang === "en") setLanguage(lang);
  };
  return (
    <CmsProvider language={currentLanguage} setLanguage={setCmsLanguage} pages={EDITABLE_PAGES}>
      {children}
    </CmsProvider>
  );
};

const TrackRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageview();
  }, [location.pathname, location.search]);

  return null;
};

const App = () => {
  const [apiError, setApiError] = useState(false);
  
  useEffect(() => {
    // Check API availability
    const checkApi = async () => {
      try {
        const response = await fetch(PUBLIC_API_BASE_URL + '/check');
        if (!response.ok) {
          setApiError(true);
        }
      } catch (error) {
        setApiError(true);
      }
    };

    checkApi();
  }, []);

//  if (apiError) {
//    return <ApiErrorPage />;
//  }

  return (
    <AuthProvider>
      <LanguageProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <BrowserRouter>
              <TrackRoutes />
              <CmsBridgeMount>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/menu" element={<Menu />} />
                  <Route path="/drinks" element={<Drinks />} />
                  <Route path="/lunch" element={<Navigate to="/catering" replace />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/catering" element={<Catering />} />
                  
                  <Route path="*" element={<NotFound />} />
                  {/* Admin Routes */}
                  <Route path="/admin/login" element={
                    <PublicRoute>
                      <AdminLogin />
                    </PublicRoute>
                  } />
                  
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } />
                </Routes>
              </Suspense>
              </CmsBridgeMount>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </LanguageProvider>
    </AuthProvider>
  );
};

export default App;
